import { getValueFromPath } from "../../IndexedDb/util.js";
import { GET_INITIAL_CALL_INFO } from "../../Queries/GetInitialCallInfo.js";
import { formatNameFromPersonObj } from "../../utils/utils.js";
import { GET_SIZE_AND_WEIGHT } from "../SizeAndWeightFrame/Query/SizeAndWeightQuery.js";
import {
  GET_ARRESTS,
  GET_PERSON_INCIDENT_SPECIFICS,
  GET_TICKETS,
} from "./queries.js";

export const personHasTicketsOrArrests = async (
  person,
  incident,
  user,
  client
) => {
  let incidentTickets = incident.incidentTickets;
  let incidentArrests = incident.incidentArrests;
  let res = { hasAssociation: true, associationErrors: [] };
  if (!user.id) {
    res.associationErrors.push("user not found");
    res.hasAssociation = true;
    return res;
  }
  if (!person.id) {
    res.associationErrors.push(
      "Person not passed to personHasTicketsOrArrests"
    );
    res.hasAssociation = true;
  }
  if (!incident.id) {
    res.associationErrors.push("Incident not found");
    return res;
  }
  if (!incidentTickets) {
    console.error("Incident tickets not found. Fetching the data.");
    try {
      const { data } = await client.query({
        query: GET_TICKETS,
        variables: { incidentId: incident.id, originatorId: user.id },
        context: { timeout: 60000 },
        fetchPolicy: "network-only",
      });
      incidentTickets = getValueFromPath(data, "getTickets");
    } catch (err) {
      console.error("Error getting tickets", err);
      res.associationErrors.push("Error getting tickets", err);
      res.hasAssociation = true;
      return res;
    }
  }
  if (!incidentArrests) {
    console.error("Incident arrests not found. Fetching the data.");
    try {
      const { data } = await client.query({
        query: GET_ARRESTS,
        variables: { incidentId: incident.id, originatorId: user.id },
        context: { timeout: 60000 },
        fetchPolicy: "network-only",
      });
      incidentArrests = getValueFromPath(data, "getArrests");
    } catch (err) {
      console.error("Error getting arrests", err);
      res.associationErrors.push("Error getting arrests", err);
      res.hasAssociation = true;
      return res;
    }
  }
  const personHasTickets = incidentTickets.some(
    ticket => ticket?.issuedTo?.id === person.id
  );
  const personHasArrests = incidentArrests.some(
    arrest => arrest?.arrestee?.id === person.id
  );
  if (personHasTickets || personHasArrests) {
    const personName = formatNameFromPersonObj(person);
    res.hasAssociation = true;
    if (personHasTickets) {
      res.associationErrors.push(
        `You cannot remove ${personName} from the incident because that person has been issued a ticket.`
      );
    }
    if (personHasArrests) {
      res.associationErrors.push(
        `You cannot remove ${personName} from the incident because that person has been arrested.`
      );
    }
  } else {
    res.hasAssociation = false;
  }
  return res;
};

export const personHasSwAssociation = async (
  person,
  incident,
  user,
  client
) => {
  let sw = incident?.sizeAndWeight;
  let res = { hasSwAssociation: true, swAssociationErrors: [] };

  if (!user.id) {
    res.swAssociationErrors.push("user not found");
    res.hasSwAssociation = true;
    return res;
  }
  if (!person.id) {
    res.swAssociationErrors.push("Person not passed to personHasSwAssociation");
    res.hasSwAssociation = true;
  }
  if (!incident.id) {
    res.swAssociationErrors.push("Incident not found");
    return res;
  }
  if (!sw?.id) {
    try {
      const { data } = await client.query({
        query: GET_INITIAL_CALL_INFO,
        variables: { incidentId: incident.id, originatorId: user.id },
        context: { timeout: 60000 },
        fetchPolicy: "network-only",
      });
      sw = getValueFromPath(data, "initialCallInfo.sizeAndWeight");
    } catch (err) {
      console.error("Error getting the size and weight ID", err);
      res.swAssociationErrors.push("Error getting the size and weight ID", err);
      res.hasSwAssociation = true;
      return res;
    }
  }
  if (sw?.id) {
    try {
      const { data } = await client.query({
        query: GET_SIZE_AND_WEIGHT,
        variables: { sizeAndWeightId: sw.id, originatorId: user.id },
        context: { timeout: 60000 },
        fetchPolicy: "network-only",
      });
      sw = getValueFromPath(data, "getSizeAndWeight");
    } catch (err) {
      console.error("Error fetching the size and weight object", err);
      res.swAssociationErrors.push(
        "Error fetching the size and weight object",
        err
      );
      res.hasSwAssociation = true;
      return res;
    }
  }
  const personHasAssociation = [
    "issuedTo",
    "operator",
    "carrier",
    "owner",
  ].some(key => sw?.[key]?.id === person.id);

  if (personHasAssociation) {
    const personName = formatNameFromPersonObj(person);
    res.hasSwAssociation = true;
    res.swAssociationErrors.push(
      `You cannot remove ${personName} from the incident because that person has an association in the Size and Weight frame.`
    );
  } else {
    res.hasSwAssociation = false;
  }

  return res;
};

export const isPisEmpty = async (pis, incident, user, client) => {
  let personIncidentSpecific = pis;
  if (!personIncidentSpecific) {
    console.error("PersonIncidentSpecifics not found. Fetching the data.");
    try {
      const { data } = await client.query({
        query: GET_PERSON_INCIDENT_SPECIFICS,
        variables: { incidentId: incident.id, originatorId: user.id },
        fetchPolicy: "network-only",
      });
      const personIncidentSpecifics = getValueFromPath(
        data,
        "getIncident.personIncidentSpecifics"
      );
      personIncidentSpecific =
        personIncidentSpecifics[personIncidentSpecifics.length - 1];
    } catch (err) {
      console.error("Error getting tickets", err);
      return true;
    }
  }
  const { firstName, lastName } =
    personIncidentSpecific?.person?.personalAttribute || {};
  const rval = !firstName && !lastName;
  return rval;
};
